import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { requestHelpers } from '../../services/api';
import DashboardLayout from '../Layout/DashboardLayout';
import SuccessOverlay from '../Common/SuccessOverlay';
import { toast } from 'react-toastify';
import './Wallet.css';

const Wallet = () => {
    const { user, refreshProfile } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rechargeAmount, setRechargeAmount] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        // Load Razorpay Script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);

        fetchTransactions();
        checkPaymentStatus();

        return () => {
            document.body.removeChild(script);
        }
    }, []);

    const checkPaymentStatus = async () => {
        // Existing URL check for fallback if needed
    };

    const fetchTransactions = async () => {
        try {
            const response = await requestHelpers.get('/payments/transactions');
            if (response.success) {
                setTransactions(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
        }
    };

    const handleAmountClick = (amount) => {
        const current = parseFloat(rechargeAmount) || 0;
        setRechargeAmount((current + amount).toString());
    };

    const handleRecharge = async (amount) => {
        const rechargeAmt = amount || rechargeAmount;
        if (!rechargeAmt || rechargeAmt < 1) {
            alert('Please enter a valid amount');
            return;
        }

        setIsProcessing(true);
        try {
            // 1. Create Order on Backend
            const res = await requestHelpers.post('/payments/create-order', {
                amount: parseFloat(rechargeAmt)
            });

            if (!res.success) {
                throw new Error(res.message || 'Failed to create order');
            }

            // 2. Initialize Razorpay Modal
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_SbJ8jhuMpItc0g', // Fallback to provided live key
                amount: res.amount,
                currency: res.currency,
                name: 'MessagePe',
                description: 'Wallet Recharge',
                order_id: res.orderId,
                notes: {
                    user_email: user.email
                },
                handler: async (response) => {
                    try {
                        const verifyRes = await requestHelpers.post('/payments/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            amount: rechargeAmt
                        });

                        if (verifyRes.success) {
                            setShowConfetti(true);
                            toast.success('🎉 Wallet recharged successfully!', {
                                position: "top-center",
                                autoClose: 5000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                theme: "colored",
                            });
                            setRechargeAmount('');
                            await refreshProfile();
                            fetchTransactions();
                        } else {
                            toast.error('Payment verification failed: ' + verifyRes.message);
                        }
                    } catch (error) {
                        console.error('Verification Error:', error);
                        toast.error(`Error verifying payment: ${error.message || 'Server error'}`);
                    }
                },
                prefill: {
                    name: user.name,
                    email: user.email
                },
                theme: {
                    color: '#2563eb'
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response) {
                toast.error('Payment failed: ' + response.error.description);
            });
            rzp.open();

        } catch (error) {
            console.error('Recharge error:', error);
            alert('Failed to initiate recharge: ' + error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownloadStatement = async () => {
        try {
            const { startDate, endDate } = dateRange;
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payments/export?startDate=${startDate}&endDate=${endDate}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) throw new Error('Download failed');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `wallet_statement_${startDate}_to_${endDate}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success('Statement downloaded successfully');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download statement');
        }
    };

    return (
        <DashboardLayout>
            <SuccessOverlay 
                active={showConfetti} 
                onComplete={() => setShowConfetti(false)} 
                title="Wallet Recharged!"
                subtitle={`Successfully added ₹${rechargeAmount || 'funds'} to your balance. You can now use this for your campaigns.`}
                type="wallet"
            />
            <div className="wallet-page">
                <div className="page-header">
                    <div className="header-content">
                        <h1>My Wallet</h1>
                        <p>Manage your balance and view transaction history</p>
                    </div>
                </div>

                <div className="wallet-grid">
                    <div className="balance-card">
                        <span className="balance-label">Current Balance</span>
                        <span className="balance-amount">₹{user.walletBalance?.toFixed(2) || '0.00'}</span>

                        <div className="recharge-options">
                            <button className="recharge-btn" onClick={() => handleAmountClick(1000)}>+ ₹1000</button>
                            <button className="recharge-btn" onClick={() => handleAmountClick(5000)}>+ ₹5000</button>
                            <button className="recharge-btn" onClick={() => handleAmountClick(10000)}>+ ₹10000</button>
                            <button className="recharge-btn" onClick={() => handleAmountClick(25000)}>+ ₹25000</button>
                        </div>

                        <div className="custom-recharge">
                            <input
                                type="number"
                                placeholder="Custom Amount"
                                value={rechargeAmount}
                                onChange={(e) => setRechargeAmount(e.target.value)}
                            />
                            <button
                                className="add-funds-btn"
                                disabled={isProcessing || !rechargeAmount}
                                onClick={() => handleRecharge()}
                            >
                                Add Funds
                            </button>
                        </div>
                    </div>

                    <div className="transactions-card">
                        <div className="card-header-actions">
                            <h2>Transaction History</h2>
                            <div className="statement-controls">
                                <div className="date-inputs">
                                    <input 
                                        type="date" 
                                        value={dateRange.startDate} 
                                        onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })} 
                                    />
                                    <span>to</span>
                                    <input 
                                        type="date" 
                                        value={dateRange.endDate} 
                                        onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })} 
                                    />
                                </div>
                                <button className="btn-download" onClick={handleDownloadStatement}>
                                    <i className="fas fa-download"></i> Download Statement
                                </button>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table className="transaction-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Transaction ID</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length > 0 ? (
                                        transactions.map(tx => (
                                            <tr key={tx._id}>
                                                <td>{new Date(tx.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <span 
                                                        className="transaction-id-badge" 
                                                        title={tx.metadata?.paymentId || tx._id} 
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(tx.metadata?.paymentId || tx._id);
                                                            toast.info('ID copied to clipboard');
                                                        }}
                                                        style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px', background: 'var(--color-secondary)', padding: '2px 6px', borderRadius: '4px' }}
                                                    >
                                                        {tx.metadata?.paymentId ? `razor_${tx.metadata.paymentId.substring(tx.metadata.paymentId.length - 8)}` : tx._id.substring(tx._id.length - 8)}
                                                        <i className="fas fa-copy ml-1" style={{ opacity: 0.5 }}></i>
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`type-badge type-${tx.type}`}>
                                                        {tx.type}
                                                    </span>
                                                </td>
                                                <td>{tx.description}</td>
                                                <td>
                                                    <span className={`amount-${tx.type}`}>
                                                        {tx.type === 'credit' ? '+' : '-'}₹{tx.amount.toFixed(2)}
                                                    </span>
                                                </td>
                                                <td>{tx.status}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" style={{ textAlign: 'center', padding: '24px' }}>
                                                No transactions found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Wallet;

